import type { components } from "../../shared/api/generated/trading-engine.js";
import {
  isMonthlyStrategy,
  requiredBacktestInterval,
  type BacktestInterval,
  type BotRevisionView,
} from "./models.js";
import monthlyDefaults from "./templates/monthly-trend-cash/backtest-defaults.json";

export type BacktestRequest = components["schemas"]["BacktestRequest"];

export interface BacktestFormValues {
  readonly adjustment: "all" | "raw" | "split";
  readonly commissionPerShareUsd: string;
  readonly minimumCommissionUsd?: string;
  readonly fillTiming: "bar-close" | "next-bar-open";
  readonly from: string;
  readonly initialCashUsd: string;
  readonly interval: BacktestInterval;
  readonly slippageBasisPoints: number;
  readonly to: string;
}

function dateDaysAgo(days: number): string {
  const date = new Date();
  date.setUTCDate(date.getUTCDate() - days);
  return date.toISOString().slice(0, 10);
}

export const intervalLabels: Readonly<Record<BacktestInterval, string>> = {
  "1m": "1 minute",
  "5m": "5 minutes",
  "15m": "15 minutes",
  "1h": "1 hour",
  "1d": "1 day",
};

export const allIntervals = Object.keys(intervalLabels) as BacktestInterval[];
export const monthlySourceIntervals: readonly BacktestInterval[] = ["1m", "5m", "15m", "1d"];

export class UnsupportedMonthlyIntervalError extends Error {
  constructor() {
    super(
      "Monthly trend/cash supports 1-minute, 5-minute, 15-minute or daily source bars. Choose one of these intervals; 1-hour bars are not supported.",
    );
    this.name = "UnsupportedMonthlyIntervalError";
  }
}

export function supportedBacktestIntervals(
  revision: BotRevisionView | undefined,
): readonly BacktestInterval[] {
  if (isMonthlyStrategy(revision?.strategy)) return monthlySourceIntervals;
  const required = requiredBacktestInterval(revision);
  return required === undefined ? allIntervals : [required];
}

export function defaultBacktestFormValues(interval: BacktestInterval = "1m"): BacktestFormValues {
  return {
    adjustment: "all",
    commissionPerShareUsd: "0",
    fillTiming: "next-bar-open",
    from: dateDaysAgo(30),
    initialCashUsd: "100000",
    interval,
    slippageBasisPoints: 0,
    to: dateDaysAgo(0),
  };
}

export function strategyBacktestFormValues(
  revision: BotRevisionView | undefined,
): BacktestFormValues {
  const defaults = defaultBacktestFormValues(requiredBacktestInterval(revision));
  if (revision === undefined || !isMonthlyStrategy(revision.strategy)) return defaults;
  const costs = monthlyDefaults.slippageBasisPointsBySymbol;
  return {
    ...defaults,
    adjustment: "raw",
    commissionPerShareUsd: monthlyDefaults.commissionPerShareUsd,
    minimumCommissionUsd: monthlyDefaults.minimumCommissionUsd,
    fillTiming: "next-bar-open",
    initialCashUsd: monthlyDefaults.initialCashUsd,
    interval: "1m",
    slippageBasisPoints: costs[revision.symbol as keyof typeof costs] ?? 2,
  };
}

// Mechanical execution constraints only; preserve supported intervals, costs and dates.
export function backtestRequestForRevision(
  values: BacktestFormValues,
  expectedVersion: number,
  revision: BotRevisionView | undefined,
): BacktestRequest {
  const monthly = isMonthlyStrategy(revision?.strategy);
  if (monthly && !monthlySourceIntervals.includes(values.interval)) {
    throw new UnsupportedMonthlyIntervalError();
  }
  return backtestRequest(
    monthly ? { ...values, adjustment: "raw", fillTiming: "next-bar-open" } : values,
    expectedVersion,
    requiredBacktestInterval(revision) ?? values.interval,
  );
}

export function backtestRequest(
  values: BacktestFormValues,
  expectedVersion: number,
  interval: BacktestInterval = values.interval,
): BacktestRequest {
  return {
    adjustment: values.adjustment,
    commissionPerShareUsd: values.commissionPerShareUsd,
    ...(values.minimumCommissionUsd?.trim()
      ? { minimumCommissionUsd: values.minimumCommissionUsd.trim() }
      : {}),
    expectedVersion,
    fillTiming: values.fillTiming,
    from: `${values.from}T00:00:00.000Z`,
    initialCashUsd: values.initialCashUsd,
    interval,
    slippageBasisPoints: Number(values.slippageBasisPoints),
    to: `${values.to}T23:59:59.999Z`,
  };
}
